/**
 * One document — §7.
 *
 * The rule this page is most likely to get wrong is §7.4's: readers resolve to
 * the published version, editors with access see the draft. **That decision is
 * the API's and it is already made** — `resolveBody` picks the body and reports
 * which one it picked as `body.source`, and `viewer.seesWorkingDraft` says
 * whether this caller is one of "editors with access". So nothing here compares
 * a status, reads a grant, or asks who is looking; it renders what came back and
 * labels it. Re-deciding client-side would produce a screen that disagrees with
 * the API about what a person is reading, which is worse than either answer.
 *
 * The editor is mounted for every viewer and made read-only for the ones who are
 * not editing, rather than swapped for a separate renderer. One rendering path
 * means a reader and an author cannot be looking at two different
 * interpretations of the same block tree — which is the whole reason §5.1 keeps
 * the component set closed.
 *
 * Review dates get the most visual weight on the page after the body itself.
 * §7.2 calls the owner/review-date pair the highest-leverage thing in the build,
 * and a lapsed document has to be *visibly* lapsed; `review.tsx` carries that,
 * including the rule that a `needs_review` document is never dimmed or gated.
 */

import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Form } from 'react-aria-components';
import {
  documentMarkdownQuery,
  useArchiveDocument,
  useDocument,
  useSpace,
  useUnarchiveDocument,
  useUpdateDocument,
} from '../../api/queries.ts';
import type { DocumentResponse, PublishResponse, UnpublishResponse } from '../../api/types.ts';
import { formatDateTime } from '../../components/format.ts';
import { Badge, StatusBadge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState, Loading, PageError } from '../../components/ui/feedback.tsx';
import { TextField } from '../../components/ui/field.tsx';
import { Card, Definition, DefinitionList, Mono, PageHeader } from '../../components/ui/layout.tsx';
import { DocumentEditor } from '../../editor/document-editor.tsx';
import { PresenceBar } from '../../editor/presence.tsx';
import { AcknowledgmentCard } from '../acknowledgments/index.ts';
import { ApprovalCard } from '../approvals/index.ts';
import { AuthorsCard } from './authors.tsx';
import { DocumentLinkButton, useDocumentNodeId } from './navigation.tsx';
import { PublishDialog, PublishedNotice } from './publish.tsx';
import { isReviewable, ReviewBanner, ReviewDate, ReviewDateDialog } from './review.tsx';
import { UnpublishDialog, UnpublishedNotice } from './unpublish.tsx';
import { ContentSourceBadge, DocumentStatusBadge, DocumentTypeBadge } from './vocabulary.tsx';

function RenameForm({
  nodeId,
  title,
  onClose,
}: {
  nodeId: string;
  title: string;
  onClose: () => void;
}) {
  const update = useUpdateDocument(nodeId);
  const [value, setValue] = useState(title);

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        update.mutate({ title: value.trim() }, { onSuccess: onClose });
      }}
    >
      <DialogBody>
        <TextField label="Title" value={value} onChange={setValue} isRequired autoFocus />
        <p className="text-sm text-tertiary">
          The node is renamed and the trigger mirrors it into the document row, which is what search
          weights highest. One writer, so the two cannot drift.
        </p>
        {update.isError ? <ErrorState error={update.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={update.isPending}
          isDisabled={value.trim() === '' || value.trim() === title}
        >
          Rename
        </Button>
      </DialogFooter>
    </Form>
  );
}

/**
 * Copy-as-markdown — §12 lists it in 1a alongside the raw endpoint.
 *
 * It fetches rather than reusing whatever the page already has, because the two
 * are not the same thing: the page holds the JSON, and §5.1's wire format is the
 * *derived* markdown the API serialises. The toast names the body that was
 * copied, since an editor copying "the document" and getting the published
 * version instead of their draft would have no way to tell.
 */
function CopyMarkdown({ nodeId }: { nodeId: string }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const copy = async () => {
    setBusy(true);
    try {
      const { text, source } = await queryClient.fetchQuery(documentMarkdownQuery(nodeId));
      if (!navigator.clipboard) throw new Error('This browser will not give the page a clipboard.');
      await navigator.clipboard.writeText(text);
      setState({
        tone: 'success',
        message: `Copied the ${source === 'draft' ? 'working draft' : (source ?? 'document')} as markdown.`,
      });
    } catch (error) {
      setState({
        tone: 'error',
        message: error instanceof Error ? error.message : 'Could not copy.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="secondary" isPending={busy} onPress={() => void copy()}>
        Copy as markdown
      </Button>
      {state ? (
        <span
          role="status"
          className={
            state.tone === 'success' ? 'text-xs text-success-primary' : 'text-xs text-error-primary'
          }
        >
          {state.message}
        </span>
      ) : null}
    </>
  );
}

/** What §7.4's two-column arrangement means for the person currently looking. */
function BodyNotice({ data }: { data: DocumentResponse }) {
  const { document, body, viewer } = data;

  if (body.source === 'version') {
    return (
      <Alert tone="info" title={`Reading version ${body.versionNumber}`}>
        This is a historical body, not what the document says now.
      </Alert>
    );
  }

  // Only editors are told a draft exists. A reader resolves to the published
  // version by design (§7.4) and has nothing to do with the information.
  if (body.source === 'draft' && viewer.seesWorkingDraft && document.publishedVersionId) {
    return (
      <Alert tone="info" title="You are reading the working draft">
        Everyone else resolves to the published version until this is published. Revising a
        published document is not supposed to disturb what the team currently sees.
      </Alert>
    );
  }

  if (body.source === 'draft' && !document.publishedVersionId) {
    return (
      <Alert tone="info" title="Never published">
        Only people who can edit this can read it at all. Publishing moves it into the sibling
        shared space and gives readers a version to resolve to.
      </Alert>
    );
  }

  return null;
}

export function DocumentPage() {
  const nodeId = useDocumentNodeId();
  const query = useDocument(nodeId);
  const spaceId = query.data?.document.spaceId;
  const space = useSpace(spaceId ?? '');

  const archive = useArchiveDocument(nodeId);
  const unarchive = useUnarchiveDocument(nodeId);

  const [publishing, setPublishing] = useState(false);
  const [publishedResult, setPublishedResult] = useState<PublishResponse | null>(null);
  const [unpublishing, setUnpublishing] = useState(false);
  const [unpublishedResult, setUnpublishedResult] = useState<UnpublishResponse | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [settingReviewDate, setSettingReviewDate] = useState(false);
  const [dirty, setDirty] = useState(false);

  if (query.isPending) return <Loading />;
  if (query.isError) {
    return (
      <PageError
        error={query.error}
        title="Document"
        back={{ href: '/schema/spaces', label: 'Back to spaces' }}
      />
    );
  }

  const { document, body, owner, viewer } = query.data;

  /**
   * Editable when the API handed back the working draft *and* said this caller
   * is one of the editors — the same two facts, not a re-derivation of either.
   * A historical body is never editable: there is nowhere for the keystrokes to
   * go, since versions are immutable.
   */
  const editable = viewer.seesWorkingDraft && body.source === 'draft';

  return (
    <>
      <PageHeader
        breadcrumb={
          <span className="flex items-center gap-1.5">
            <Link to="/schema/spaces" className="text-tertiary no-underline">
              Spaces
            </Link>
            <span aria-hidden="true">/</span>
            <Link
              to="/spaces/$spaceId"
              params={{ spaceId: document.spaceId }}
              className="text-tertiary no-underline"
            >
              {space.data?.space.name ?? 'Space'}
            </Link>
          </span>
        }
        title={document.title}
        actions={
          <>
            <PresenceBar nodeId={nodeId} />
            <DocumentLinkButton nodeId={nodeId} to="/documents/$nodeId/versions">
              History
            </DocumentLinkButton>
            {document.status === 'archived' ? (
              <Button
                variant="secondary"
                isPending={unarchive.isPending}
                onPress={() => unarchive.mutate()}
              >
                Unarchive
              </Button>
            ) : (
              <>
                {/* §7.4 hangs `archived` off `published`, and the server refuses
                    the transition for a document that has never been published.
                    Nothing is hidden by drawing the button anyway — it is just
                    an action that cannot succeed, and the answer to an unwanted
                    draft is to delete it. */}
                {document.publishedVersionId ? (
                  <Button variant="secondary" onPress={() => setArchiving(true)}>
                    Archive
                  </Button>
                ) : null}
                {/* Phase 2 §4.4. Drawn from where the node *is* rather than
                    from its status, because that is what unpublishing changes:
                    a document sitting in a client space is one the client can
                    find, whatever the lifecycle column says. The dialog is what
                    explains the refusal once they have engaged — the button
                    stays live so the explanation is reachable. */}
                {space.data?.space.kind === 'client' ? (
                  <Button variant="secondary" onPress={() => setUnpublishing(true)}>
                    Unpublish
                  </Button>
                ) : null}
                <Button variant="primary" onPress={() => setPublishing(true)}>
                  Publish
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <DocumentTypeBadge type={document.type} />
        <DocumentStatusBadge status={document.status} />
        <ContentSourceBadge source={body.source} versionNumber={body.versionNumber} />
        {document.reviewBy ? (
          <span className="text-xs text-tertiary">
            <ReviewDate document={document} />
          </span>
        ) : null}
        {editable ? (
          <span className="ml-auto text-xs text-tertiary" role="status">
            {dirty ? 'Syncing…' : 'All changes saved'}
          </span>
        ) : null}
      </div>

      {publishedResult ? <PublishedNotice result={publishedResult} /> : null}
      {unpublishedResult ? <UnpublishedNotice result={unpublishedResult} /> : null}
      {archive.isError ? <ErrorState error={archive.error} /> : null}
      {unarchive.isError ? <ErrorState error={unarchive.error} /> : null}

      {/* §7.2's surface. Deliberately above the body and deliberately not in
          front of it — the document stays fully readable while it complains. */}
      <ReviewBanner document={document} canEdit={editable && isReviewable(document.type)} />

      {document.status === 'archived' ? (
        <Alert tone="info" title="Archived">
          Out of circulation rather than gone: excluded from search unless someone asks for archived
          results, and dropped from the past-review list. This link still resolves, which is the
          whole distinction between archiving and deleting.
        </Alert>
      ) : null}

      <BodyNotice data={query.data} />

      <div className="grid gap-6 lg:grid-cols-[3fr_1fr]">
        <Card>
          <DocumentEditor
            nodeId={nodeId}
            editable={editable}
            initialContent={body.content}
            onDirtyChange={setDirty}
          />
        </Card>

        <div className="flex flex-col gap-6">
          <Card>
            <h2 className="mb-4 text-md font-semibold text-primary">Document</h2>
            <DefinitionList>
              <Definition term="Owner">
                {owner ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <Link to="/admin/people/$personId" params={{ personId: owner.id }}>
                      {owner.name}
                    </Link>
                    <StatusBadge status={owner.status} />
                  </span>
                ) : (
                  <Mono>{document.ownerId}</Mono>
                )}
              </Definition>
              <Definition term="Review">
                <span className="flex flex-col items-start gap-1">
                  <ReviewDate document={document} />
                  {editable && isReviewable(document.type) ? (
                    <Button
                      size="sm"
                      variant="tertiary"
                      className="px-0"
                      onPress={() => setSettingReviewDate(true)}
                    >
                      {document.reviewBy ? 'Change the date' : 'Set a review date'}
                    </Button>
                  ) : null}
                </span>
              </Definition>
              <Definition term="Published version">
                {document.publishedVersionId ? (
                  <Link to="/documents/$nodeId/versions" params={{ nodeId }}>
                    <Mono>{document.publishedVersionId}</Mono>
                  </Link>
                ) : (
                  <span className="text-tertiary">None yet</span>
                )}
              </Definition>
              <Definition term="Working draft">
                {document.hasWorkingDraft ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <Badge tone="outline">exists</Badge>
                    <span className="text-xs text-tertiary">
                      {formatDateTime(document.draftUpdatedAt)}
                    </span>
                  </span>
                ) : (
                  <span className="text-tertiary">None — nothing unpublished</span>
                )}
              </Definition>
              <Definition term="Created">{formatDateTime(document.createdAt)}</Definition>
              <Definition term="Updated">{formatDateTime(document.updatedAt)}</Definition>
              <Definition term="Identifier">
                <Mono>{document.nodeId}</Mono>
              </Definition>
            </DefinitionList>
            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-secondary pt-4">
              <CopyMarkdown nodeId={nodeId} />
              {editable ? (
                <Button size="sm" variant="tertiary" onPress={() => setRenaming(true)}>
                  Rename
                </Button>
              ) : null}
              <Link to="/schema/access/nodes/$nodeId" params={{ nodeId }} className="text-sm link">
                Who can reach this
              </Link>
            </div>
          </Card>

          {/* §7.6 above §7.7 and both above the byline, because that is the
              order they gate each other in: nothing can be acknowledged until it
              is published, and as of 1b nothing publishes until it is approved.
              The gate has to be visible beside the Publish button rather than
              only inside the dialog that refuses — a refusal a person meets for
              the first time at the moment they press the button is a refusal
              they read as a bug. */}
          <ApprovalCard
            nodeId={nodeId}
            title={document.title}
            ownerName={owner?.name ?? null}
            canRequest={editable}
          />

          <AcknowledgmentCard
            nodeId={nodeId}
            title={document.title}
            documentStatus={document.status}
            // The server's answer, not `editable`. §10.3 gives a contractor
            // `document.edit` and not `document.publish`, and opening a cycle is
            // the latter — drawing this from the edit signal would put the
            // button in front of the one person the split exists to keep it away
            // from.
            canOpen={viewer.canPublish}
          />

          <AuthorsCard nodeId={nodeId} canEdit={editable} />
        </div>
      </div>

      <PublishDialog
        document={document}
        isOpen={publishing}
        onOpenChange={setPublishing}
        onPublished={setPublishedResult}
      />

      <UnpublishDialog
        document={document}
        isOpen={unpublishing}
        onOpenChange={setUnpublishing}
        onUnpublished={setUnpublishedResult}
      />

      <ReviewDateDialog
        document={document}
        isOpen={settingReviewDate}
        onOpenChange={setSettingReviewDate}
      />

      <Dialog title="Rename this document" isOpen={renaming} onOpenChange={setRenaming}>
        {(close) => <RenameForm nodeId={nodeId} title={document.title} onClose={close} />}
      </Dialog>

      {/* Archiving is reversible, but it takes a document out of search for
          everyone, which is not what the neighbouring Publish button does — one
          slip between two adjacent buttons should not be the whole action. */}
      <Dialog
        title="Archive this document"
        description={document.title}
        isOpen={archiving}
        onOpenChange={setArchiving}
      >
        {(close) => (
          <>
            <DialogBody>
              <p className="text-sm text-secondary">
                It leaves search — unless someone explicitly asks for archived results — and drops
                off the past-review list. It is not deleted and this link keeps working, which is
                the whole distinction between archiving and deleting. Unarchiving puts it back.
              </p>
              {archive.isError ? <ErrorState error={archive.error} /> : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="tertiary" onPress={close}>
                Cancel
              </Button>
              <Button
                variant="primary"
                isPending={archive.isPending}
                onPress={() => archive.mutate(undefined, { onSuccess: close })}
              >
                Archive
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </>
  );
}
